import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { createUser } from '../../api/usersApi.js';
import { listRoles } from '../../api/rolesApi.js';
import { listVendors, type VendorSummary } from '../../api/vendorsApi.js';
import { useTenantIdentityState } from '../../shell/useTenantIdentity.js';

const LOGIN_SUFFIX = /^[A-Za-z0-9_]{2,40}$/;
const MOBILE = /^[6-9][0-9]{9}$/;
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function FieldForceCreatePage(): ReactNode {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { identity: tenant, error: tenantError } = useTenantIdentityState(token);
  const [vendors, setVendors] = useState<readonly VendorSummary[]>([]);
  const [vendorId, setVendorId] = useState('');
  const [painterName, setPainterName] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [loginSuffix, setLoginSuffix] = useState('');
  const [password, setPassword] = useState('');
  const [roleId, setRoleId] = useState<string | null>(null);
  // The login needs the WORKFORCE role; without it nothing may be created.
  const [rolesRead, setRolesRead] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // A login id without the tenant prefix must never be created, so Save
  // waits for the tenant to be known.
  const prefix = tenant?.tenantCode ? `${tenant.tenantCode}_` : '';
  useEffect(() => {
    let alive = true;
    void listVendors(token)
      .then((v) => { if (alive) setVendors(v); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    void listRoles(token)
      .then((rs) => {
        if (!alive) return;
        setRoleId(rs.find((r) => r.roleCode === 'WORKFORCE')?.roleId ?? null);
        setRolesRead(true);
      })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  const canSave = !busy && !!prefix && !!roleId;

  async function submit() {
    if (!prefix || !roleId) return;
    if (!vendorId) { setError('Select the vendor this field force member works under.'); return; }
    if (!painterName.trim()) { setError('Name is required.'); return; }
    if (!MOBILE.test(mobileNumber)) { setError('Mobile must be 10 digits starting 6-9.'); return; }
    if (!EMAIL.test(email.trim())) { setError('A valid email is required.'); return; }
    if (!address.trim()) { setError('Address is required.'); return; }
    if (!LOGIN_SUFFIX.test(loginSuffix.trim())) { setError('Login id must be 2-40 letters, digits or underscore.'); return; }
    if (password.length < 8) { setError('Temporary password must be at least 8 characters.'); return; }

    setError(null); setBusy(true);
    try {
      const loginId = `${prefix}${loginSuffix.trim()}`;
      // painter_code is allocated server-side as <VENDOR_CODE>_F00001.
      const painter = await apiRequest<{ painterId: string }>('/v1/master/painters', {
        method: 'POST', token,
        body: {
          vendorId,
          painterName: painterName.trim(),
          mobileNumber: mobileNumber.trim(),
          address: address.trim(),
          skillTypes: [],
          territoryCoverage: [],
          experienceYears: 0,
          effectiveFromDate: today(),
        },
      });

      const [first, ...rest] = painterName.trim().split(/\s+/);
      await createUser(
        {
          employeeCode: loginId,
          firstName: first ?? painterName.trim(),
          lastName: rest.join(' ') || painterName.trim(),
          mobileNumber: `+91${mobileNumber.trim()}`,
          emailAddress: email.trim(),
          temporaryPassword: password,
          effectiveFromDate: today(),
          userType: 'PAINTER',
          vendorId,
          defaultRoleId: roleId,
        },
        token,
      );

      navigate(`/admin/mdm/painters?created=${painter.painterId}`);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }

  return (
    <main aria-label="field-force-new">
      <h1>New Field Force member</h1>
      {tenantError ? <p role="alert" aria-label="tenant-error">{tenantError}</p> : null}
      {rolesRead && !roleId ? (
        <p role="alert" aria-label="role-missing">No WORKFORCE role is configured, so this login cannot be given one.</p>
      ) : null}
      {error ? <p role="alert">{error}</p> : null}

      <label htmlFor="ff-vendor">Works under vendor</label>
      <select id="ff-vendor" aria-label="vendor" value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
        <option value="">Select a vendor</option>
        {vendors.map((v) => (
          <option key={v.vendorId} value={v.vendorId}>{v.vendorCode} — {v.vendorName}</option>
        ))}
      </select>

      <label htmlFor="ff-name">Name</label>
      <input id="ff-name" aria-label="name" value={painterName} onChange={(e) => setPainterName(e.target.value)} />

      <label htmlFor="ff-mob">Phone number</label>
      <input id="ff-mob" aria-label="mobile" value={mobileNumber} onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, ''))} />

      <label htmlFor="ff-em">Email</label>
      <input id="ff-em" type="email" aria-label="email" value={email} onChange={(e) => setEmail(e.target.value)} />

      <label htmlFor="ff-addr">Address</label>
      <input id="ff-addr" aria-label="address" value={address} onChange={(e) => setAddress(e.target.value)} />

      <fieldset aria-label="field-force-login">
        <legend>Login</legend>
        <label htmlFor="ff-login">Login ID</label>
        <span>
          <span aria-label="login-id-prefix"><strong>{prefix}</strong></span>
          <input id="ff-login" aria-label="login-suffix" value={loginSuffix} onChange={(e) => setLoginSuffix(e.target.value)} />
        </span>
        <label htmlFor="ff-pass">Temporary password</label>
        <input id="ff-pass" type="password" aria-label="temp-password" value={password} onChange={(e) => setPassword(e.target.value)} />
      </fieldset>

      <button onClick={submit} disabled={!canSave} aria-label="create-field-force">
        {busy ? 'Saving…' : 'Create field force member'}
      </button>
    </main>
  );
}
